import React, { Component, ReactElement } from 'react';

import { AvatarAPI } from '@philips-emr/tws-components-api';
import { Direction, Size } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { array, bool, expression, oneOf, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues } from '@philips-emr/tws-utils';

import withRouterContext, { RouterContextProps } from '../with-router-context/with-router-context';

import './avatar.scss';

export interface AvatarProps {
  className?: string;
  direction: Direction;
  directionBreakpoints: string[];
  image: string;
  size: Size;
  sizeBreakpoints: string[];
  subtitle: string;
  subtitleHiddenOnMobile?: boolean;
  title: string;
}

@AwesomeComponent({ type: ComponentType.DATA_DISPLAY, api: AvatarAPI })
class Avatar extends Component<AvatarProps> {

  static propTypes = {
    className: string,
    direction: oneOf(enumValues(Direction)),
    directionBreakpoints: array.notEnumerable(),
    image: string,
    size: oneOf(enumValues(Size)),
    sizeBreakpoints: array.notEnumerable(),
    subtitle: expression,
    subtitleHiddenOnMobile: bool,
    title: expression,
  };

  static defaultProps = {
    direction: Direction.HORIZONTAL,
    directionBreakpoints: [],
    image: '',
    size: Size.MEDIUM,
    sizeBreakpoints: [],
    subtitle: '',
    subtitleHiddenOnMobile: false,
    title: '',
  };

  renderData = (): ReactElement | null => {
    const { title, subtitle, subtitleHiddenOnMobile } = this.props;
    const showData = (title.length > 0 || subtitle.length > 0);

    const subtitleClasses = {
      'c-avatar__subtitle': true,
      'u-hide@xsm': Boolean(subtitleHiddenOnMobile),
    };

    const subtitleClassName = classNames(subtitleClasses);

    return !showData ? null : (
      <div className="c-avatar__data">
        {title && <span className="c-avatar__title">{msg(title)}</span>}
        {subtitle && <span className={subtitleClassName}>{msg(subtitle)}</span>}
      </div>
    );
  };

  renderImage(): ReactElement {
    const { image } = this.props;

    const imageComponent = ({ href }: RouterContextProps): ReactElement => {
      return <div className="c-avatar__img" 
        ref={node => node && node.style.setProperty('background-image', `url(${href})`, 'important')} 
        x-contrast="bd"/>;
    };

    const ImageElement = withRouterContext(imageComponent, image);

    return <ImageElement />;
  }

  render(): ReactElement {
    const { className, direction, directionBreakpoints, size, sizeBreakpoints } = this.props;

    const avatarClasses = {
      'c-avatar': true,
      [`${className}`]: Boolean(className),
      [`c-avatar--${direction}`]: directionBreakpoints.length === 0,
      [`c-avatar--${size}`]: sizeBreakpoints.length === 0,
    };

    const avatarBreakpoints = {
      [`c-avatar--${direction}`]: directionBreakpoints,
      [`c-avatar--${size}`]: sizeBreakpoints,
    };

    return (
      <div className={classNames(avatarClasses, avatarBreakpoints)}>
        {this.renderImage()}
        {this.renderData()}
      </div>
    );
  }
}

export default Avatar;
